(() => {
  const VALID_TABS = ['answers', 'videos', 'manuals'];
  const VALID_TOPICS = ['all', 'setup', 'runtime', 'troubleshooting', 'travel', 'care'];

  const productButtons = () => [...document.querySelectorAll('[data-support-product-v3]')];
  const productForKey = (key) => productButtons().find((button) => button.dataset.key === key);
  const normalize = (value = '') => value.trim().toLowerCase();
  const productKeys = (resource) => {
    const value = resource.dataset.products || 'all';
    if (value.trim().startsWith('[')) {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return parsed.map((key) => String(key).trim()).filter(Boolean);
      } catch (_error) {
        // Legacy comma-separated values remain supported below.
      }
    }
    return value.split(',').map((key) => key.trim()).filter(Boolean);
  };

  const readUrlState = () => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    const topic = params.get('topic');
    return {
      model: params.get('model') || '',
      tab: VALID_TABS.includes(tab) ? tab : 'answers',
      topic: VALID_TOPICS.includes(topic) ? topic : 'all',
      q: params.get('q') || ''
    };
  };

  const writeUrlState = (state) => {
    const url = new URL(window.location.href);
    const setOptional = (key, value, defaultValue = '') => {
      if (!value || value === defaultValue) url.searchParams.delete(key);
      else url.searchParams.set(key, value);
    };
    setOptional('model', state.model);
    setOptional('tab', state.tab, 'answers');
    setOptional('topic', state.topic, 'all');
    setOptional('q', state.q);
    window.history.replaceState({ easylongerSupport: true }, '', url);
  };

  const setProductButtonState = (key) => {
    productButtons().forEach((button) => {
      const selected = key !== '' && button.dataset.key === key;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    });
  };

  const dispatchProduct = (button, updateUrl = true) => {
    if (!button) return;
    setProductButtonState(button.dataset.key);
    window.dispatchEvent(new CustomEvent('easylonger:support-product', {
      detail: { ...button.dataset, updateUrl }
    }));
  };

  const initializeGlobalDelegation = () => {
    if (window.__elSupportGlobalAbort) return;
    const controller = new AbortController();
    window.__elSupportGlobalAbort = controller;
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-support-product-v3]');
      if (button) dispatchProduct(button);
    }, { signal: controller.signal });
  };

  const initializeWorkspace = (workspace) => {
    if (!workspace || workspace.dataset.supportInitialized === 'true') return;
    workspace.dataset.supportInitialized = 'true';
    const controller = new AbortController();
    workspace.__elSupportAbort = controller;
    const signal = controller.signal;
    const state = { ...readUrlState(), language: 'en' };

    const status = workspace.querySelector('[data-support-status-v3]');
    const summary = workspace.querySelector('[data-support-summary-v3]');
    const summaryEmpty = workspace.querySelector('[data-summary-empty]');
    const summarySelected = workspace.querySelector('[data-summary-selected]');
    const modelSelect = workspace.querySelector('[data-support-model-select-v3]');
    const resources = [...workspace.querySelectorAll('[data-support-resource-v3]')];

    const announce = (message) => {
      if (status) status.textContent = message;
    };

    const refreshModelSelect = () => {
      if (!modelSelect) return;
      const current = state.model;
      modelSelect.querySelectorAll('option:not(:first-child)').forEach((option) => option.remove());
      productButtons().forEach((button) => {
        const option = document.createElement('option');
        option.value = button.dataset.key;
        option.textContent = button.dataset.name;
        modelSelect.append(option);
      });
      modelSelect.value = productForKey(current) ? current : '';
    };

    const showProductSummary = (button) => {
      const hasProduct = Boolean(button);
      summary?.classList.toggle('is-empty', !hasProduct);
      if (summaryEmpty) summaryEmpty.hidden = hasProduct;
      if (summarySelected) summarySelected.hidden = !hasProduct;
      if (!hasProduct) return;

      const setText = (selector, value) => {
        const element = workspace.querySelector(selector);
        if (element) element.textContent = value || '';
      };
      setText('[data-summary-name]', button.dataset.name);
      setText('[data-summary-description]', button.dataset.description);
      setText('[data-summary-capacity]', button.dataset.capacity);
      setText('[data-summary-nights]', button.dataset.nights);
      const image = workspace.querySelector('[data-summary-image]');
      if (image) {
        image.src = button.dataset.image || '';
        image.alt = `${button.dataset.name} CPAP battery`;
      }
      const email = document.querySelector('[data-support-email-v3]');
      if (email) email.href = `mailto:${email.dataset.address}?subject=${encodeURIComponent(`Support request · ${button.dataset.name}`)}`;
    };

    const panelForResource = (resource) => {
      if (resource.dataset.resourceType === 'faq') return 'answers';
      if (resource.dataset.resourceType === 'manual') return 'manuals';
      return 'videos';
    };

    const hydrateVideoThumb = (resource) => {
      const thumb = resource.querySelector('.el-support-v3__video-thumb');
      if (!thumb || thumb.dataset.posterReady === 'true') return;
      thumb.dataset.posterReady = 'true';
      const poster = thumb.dataset.videoPoster || '';
      if (!poster) return;
      const image = document.createElement('img');
      image.src = poster;
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      image.addEventListener('error', () => image.remove(), { once: true });
      thumb.prepend(image);
    };

    const instantiateVideo = (thumb) => {
      const resource = thumb.closest('[data-support-resource-v3]');
      if (resource) {
        resource.classList.remove('is-error');
        resource.querySelector('.el-support-v3__video-error')?.remove();
      }
      const video = document.createElement('video');
      video.controls = true;
      video.playsInline = true;
      video.preload = 'metadata';
      video.width = 1280;
      video.height = 720;
      video.setAttribute('aria-label', (thumb.getAttribute('aria-label') || 'Support video').replace(/^Play video:\s*/i, ''));
      const poster = thumb.dataset.videoPoster || '';
      if (poster) video.poster = poster;
      const source = document.createElement('source');
      source.src = thumb.dataset.videoSrc;
      source.type = 'video/mp4';
      video.append(source);
      const fail = () => {
        if (!video.isConnected) return;
        // The thumb keeps its existing poster img; re-insert it as-is so no duplicate image is created.
        video.replaceWith(thumb);
        if (!resource || resource.querySelector('.el-support-v3__video-error')) return;
        resource.classList.add('is-error');
        const note = document.createElement('p');
        note.className = 'el-support-v3__video-error';
        note.setAttribute('role', 'alert');
        note.textContent = 'This video could not be loaded. Check your connection and try again, or open the manual for this battery.';
        thumb.after(note);
      };
      video.addEventListener('error', fail, { once: true });
      source.addEventListener('error', fail, { once: true });
      thumb.replaceWith(video);
      video.play().catch(() => {
        /* Autoplay can be blocked; the native controls remain available. */
      });
    };

    const applyFilters = () => {
      let visibleAnswers = 0;
      let visibleVideos = 0;
      let visibleManuals = 0;
      const query = normalize(state.q);

      resources.forEach((resource) => {
        const keys = productKeys(resource);
        const isAccessoryGuidance = resource.dataset.resourceType === 'accessory_guidance';
        const matchesProduct = isAccessoryGuidance
          ? Boolean(state.model)
          : state.model ? keys.includes('all') || keys.includes(state.model) : keys.includes('all');
        const matchesTopic = state.topic === 'all' || resource.dataset.topic === state.topic;
        const matchesQuery = query === '' || normalize(resource.textContent).includes(query);
        const panel = panelForResource(resource);
        const matchesLanguage = panel !== 'manuals' || resource.dataset.language === state.language;
        const visible = matchesProduct && matchesTopic && matchesQuery && matchesLanguage;
        resource.hidden = !visible;

        const video = resource.querySelector('video');
        if (visible && state.tab === 'videos') hydrateVideoThumb(resource);
        if ((!visible || state.tab !== 'videos') && video && !video.paused) video.pause();

        if (visible && panel === 'answers') visibleAnswers += 1;
        if (visible && panel === 'videos' && !isAccessoryGuidance) visibleVideos += 1;
        if (visible && panel === 'manuals') visibleManuals += 1;
      });

      const counts = { answers: visibleAnswers, videos: visibleVideos, manuals: visibleManuals };
      Object.entries(counts).forEach(([panel, count]) => {
        const empty = workspace.querySelector(`[data-support-empty-v3="${panel}"]`);
        if (empty) empty.hidden = count > 0;
      });

      const selected = productForKey(state.model);
      const context = selected ? selected.dataset.name : 'general help';
      const count = counts[state.tab];
      announce(`${count} ${state.tab === 'answers' ? 'answers' : state.tab} shown for ${context}.`);
    };

    const setTab = (tab, focus = false) => {
      state.tab = VALID_TABS.includes(tab) ? tab : 'answers';
      workspace.querySelectorAll('[data-support-tab-v3]').forEach((button) => {
        const selected = button.dataset.supportTabV3 === state.tab;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-selected', String(selected));
        button.tabIndex = selected ? 0 : -1;
        if (selected && focus) button.focus();
      });
      workspace.querySelectorAll('[data-support-panel-v3]').forEach((panel) => {
        panel.hidden = panel.dataset.supportPanelV3 !== state.tab;
      });
      applyFilters();
    };

    const setTopic = (topic) => {
      state.topic = VALID_TOPICS.includes(topic) ? topic : 'all';
      workspace.querySelectorAll('[data-support-filter-v3]').forEach((button) => {
        const selected = button.dataset.supportFilterV3 === state.topic;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      workspace.querySelectorAll('[data-support-video-filter-v3]').forEach((button) => {
        const selected = button.dataset.supportVideoFilterV3 === state.topic;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      applyFilters();
    };

    const setLanguage = (language) => {
      state.language = language || 'en';
      workspace.querySelectorAll('[data-support-language-v3]').forEach((button) => {
        const selected = button.dataset.supportLanguageV3 === state.language;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      applyFilters();
    };

    const setModel = (key, updateUrl = true) => {
      const button = productForKey(key);
      state.model = button ? key : '';
      setProductButtonState(state.model);
      if (modelSelect) modelSelect.value = state.model;
      showProductSummary(button);
      applyFilters();
      if (updateUrl) writeUrlState(state);
    };

    window.addEventListener('easylonger:support-product', (event) => {
      setModel(event.detail.key, event.detail.updateUrl !== false);
    }, { signal });

    window.addEventListener('easylonger:support-products-updated', () => {
      refreshModelSelect();
      setModel(state.model, false);
    }, { signal });

    window.addEventListener('popstate', () => {
      const next = readUrlState();
      state.q = next.q;
      document.querySelectorAll('[data-support-search-v3]').forEach((input) => { input.value = state.q; });
      setModel(next.model, false);
      setTopic(next.topic);
      setTab(next.tab);
    }, { signal });

    modelSelect?.addEventListener('change', () => {
      const button = productForKey(modelSelect.value);
      if (button) dispatchProduct(button);
      else setModel('', true);
    }, { signal });

    workspace.querySelectorAll('[data-support-tab-v3]').forEach((button) => {
      button.addEventListener('click', () => {
        setTab(button.dataset.supportTabV3);
        writeUrlState(state);
      }, { signal });
      button.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const tabs = [...workspace.querySelectorAll('[data-support-tab-v3]')];
        const index = tabs.indexOf(button);
        const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : event.key === 'ArrowRight' ? (index + 1) % tabs.length : (index - 1 + tabs.length) % tabs.length;
        setTab(tabs[nextIndex].dataset.supportTabV3, true);
        writeUrlState(state);
      }, { signal });
    });

    workspace.querySelectorAll('[data-support-filter-v3]').forEach((button) => button.addEventListener('click', () => {
      setTopic(button.dataset.supportFilterV3);
      writeUrlState(state);
    }, { signal }));

    workspace.querySelectorAll('[data-support-video-filter-v3]').forEach((button) => button.addEventListener('click', () => {
      setTopic(button.dataset.supportVideoFilterV3);
      writeUrlState(state);
    }, { signal }));

    workspace.querySelectorAll('[data-support-language-v3]').forEach((button) => button.addEventListener('click', () => {
      setLanguage(button.dataset.supportLanguageV3);
    }, { signal }));

    workspace.addEventListener('click', (event) => {
      const thumb = event.target.closest('.el-support-v3__video-thumb');
      if (thumb && workspace.contains(thumb)) instantiateVideo(thumb);
    }, { signal });

    workspace.querySelectorAll('[data-support-solution-v3]').forEach((button) => button.addEventListener('click', () => {
      state.q = button.dataset.query || '';
      document.querySelectorAll('[data-support-search-v3]').forEach((input) => { input.value = state.q; });
      setTopic(button.dataset.topic || 'all');
      setTab('answers');
      writeUrlState(state);

      const firstAnswer = workspace.querySelector('[data-support-resource-list-v3="answers"] [data-support-resource-v3]:not([hidden]) > button');
      if (!firstAnswer) return;
      const item = firstAnswer.parentElement;
      item.classList.add('is-open');
      firstAnswer.setAttribute('aria-expanded', 'true');
      firstAnswer.focus({ preventScroll: true });
      item.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }, { signal }));

    workspace.querySelectorAll('[data-resource-type="faq"] > button').forEach((button) => button.addEventListener('click', () => {
      const item = button.parentElement;
      const open = !item.classList.contains('is-open');
      item.classList.toggle('is-open', open);
      button.setAttribute('aria-expanded', String(open));
    }, { signal }));

    document.querySelectorAll('[data-support-search-v3]').forEach((input) => {
      input.value = state.q;
      input.addEventListener('input', () => {
        state.q = input.value.trim();
        document.querySelectorAll('[data-support-search-v3]').forEach((peer) => { if (peer !== input) peer.value = state.q; });
        applyFilters();
        writeUrlState(state);
      }, { signal });
    });

    refreshModelSelect();
    setModel(state.model, false);
    setLanguage('en');
    setTopic(state.topic);
    setTab(state.tab);
  };

  const initialize = (root = document) => {
    initializeGlobalDelegation();
    root.querySelectorAll('[data-support-workspace-v3]').forEach(initializeWorkspace);
  };

  initialize();
  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
    window.dispatchEvent(new CustomEvent('easylonger:support-products-updated'));
  });
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-support-workspace-v3]').forEach((workspace) => {
      workspace.__elSupportAbort?.abort();
      delete workspace.__elSupportAbort;
      delete workspace.dataset.supportInitialized;
    });
  });
})();
